/**
 * Social media follow links — Instagram, Facebook, X, LinkedIn, Reddit,
 * WhatsApp, Substack, YouTube, TikTok.
 *
 * All nine are live. Every URL was verified to return HTTP 200 before being
 * added; a dead link here appears in the footer of every page on the site.
 *
 * Note the handles are NOT consistent across platforms, which is a real trap:
 * Instagram and TikTok use `entre_dash` (underscore), LinkedIn and Substack use
 * `entredash`, X uses `Entre_Dash`, and YouTube uses `@EntreDash`. Do not
 * assemble one of these URLs from another platform's handle.
 *
 * Adding a platform means one entry in `PLATFORMS` and one icon; both the footer
 * row and the Settings card render from that list. Check the grid column counts
 * in `.social-footer-row` when the total changes — they are explicit, not
 * auto-fitting, so the row does not silently orphan an icon.
 *
 * Used in two places today:
 *   - SiteFooter.tsx — global footer, every public + authenticated page
 *   - Settings.tsx — Profile page, as a "Follow EntreDash" card
 *
 * Two visual variants supported:
 *   - "footer" — small icon-only row, fits inside the footer's
 *     existing typography rhythm. Brass-tinted hover.
 *   - "card" — labeled icon + handle in a bordered card. Used on the
 *     Settings page where the visitor has more screen space and
 *     attention to read context.
 */

import { useState } from 'react';

interface SocialPlatform {
  name: string;
  handle: string;
  href: string | null; // null when comingSoon = true
  comingSoon: boolean;
  icon: React.ReactNode;
}

const INSTAGRAM_HANDLE = 'entre_dash';
const INSTAGRAM_URL = 'https://www.instagram.com/entre_dash/';
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61590697092951';
const X_URL = 'https://x.com/Entre_Dash';
const LINKEDIN_URL = 'https://www.linkedin.com/company/entredash/';
// Canonical profile form (stable) rather than the mobile share token.
const REDDIT_URL = 'https://www.reddit.com/user/EntreDash/';
const WHATSAPP_URL = 'https://wa.me/19728503004';
const SUBSTACK_URL = 'https://entredash.substack.com/';
// Handle form, not /channel/UC…: the handle survives a channel rename.
const YOUTUBE_URL = 'https://www.youtube.com/@EntreDash';
// Note the underscore — the TikTok handle is entre_dash, matching Instagram,
// not the unbroken "entredash" used by LinkedIn and Substack.
const TIKTOK_URL = 'https://www.tiktok.com/@entre_dash';

// SVG icons. Sized 18px square by default; the parent style controls
// the actual rendered size via inline width/height props on the SVG.
// Stroke / fill colors use currentColor so the parent CSS controls
// the visual hue (brass at rest, parchment-on-brass on hover).

const InstagramIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
  </svg>
);

const FacebookIcon = ({ size = 18 }: { size?: number }) => (
  // Facebook glyph — the canonical "f" inside a rounded square.
  // Single-color, filled, no stroke. Uses currentColor so brass
  // theming applies the same way Instagram + LinkedIn do.
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h9.4v-7.4h-2.6v-3h2.6V9.3c0-2.55 1.55-3.95 3.83-3.95 1.09 0 2.03.08 2.3.12v2.66h-1.58c-1.24 0-1.48.59-1.48 1.45v1.91h2.96l-.39 3h-2.57V22h5.05a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2z" />
  </svg>
);

const LinkedInIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M20.5 2h-17A1.5 1.5 0 0 0 2 3.5v17A1.5 1.5 0 0 0 3.5 22h17a1.5 1.5 0 0 0 1.5-1.5v-17A1.5 1.5 0 0 0 20.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 1 1 8.3 6.5a1.78 1.78 0 0 1-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0 0 13 14.19a.66.66 0 0 0 0 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 0 1 2.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
  </svg>
);

const XIcon = ({ size = 18 }: { size?: number }) => (
  // X (Twitter) glyph — the official X logo is a stylized italic-x
  // form. Using the public mark since the platform did not register
  // a new icon that's both clearly identifiable and copyright-clear.
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const RedditIcon = ({ size = 18 }: { size?: number }) => (
  // Reddit Snoo mark inside the circular logo, single-color filled.
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.8 11.33c.02.18.04.36.04.55 0 2.8-3.26 5.07-7.28 5.07s-7.28-2.27-7.28-5.07c0-.19.01-.37.04-.55a1.62 1.62 0 0 1-.92-1.46 1.63 1.63 0 0 1 2.76-1.17 7.93 7.93 0 0 1 4.33-1.37l.82-3.86a.35.35 0 0 1 .42-.27l2.7.57a1.15 1.15 0 1 1-.15.67l-2.4-.5-.73 3.45a7.93 7.93 0 0 1 4.27 1.38 1.63 1.63 0 1 1 1.84 2.63 1.6 1.6 0 0 1-.18.13zM8.7 13.3a1.07 1.07 0 1 0 2.14 0 1.07 1.07 0 0 0-2.14 0zm6.6 0a1.07 1.07 0 1 0-2.14 0 1.07 1.07 0 0 0 2.14 0zm-1.06 2.62a.36.36 0 0 0-.5.01c-.45.45-1.4.6-2.04.6-.65 0-1.6-.15-2.04-.6a.36.36 0 1 0-.5.5c.7.71 1.93.8 2.54.8.6 0 1.83-.09 2.53-.8a.36.36 0 0 0 .01-.5z" />
  </svg>
);

const WhatsAppIcon = ({ size = 18 }: { size?: number }) => (
  // WhatsApp glyph — speech bubble with a handset, single-color filled.
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.6 6.32A7.85 7.85 0 0 0 12.04 4a7.94 7.94 0 0 0-6.9 11.9L4 20l4.2-1.1a7.93 7.93 0 0 0 3.83.98h.01a7.94 7.94 0 0 0 5.56-13.56zM12.05 18.55a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.5.65.67-2.43-.16-.25a6.59 6.59 0 1 1 5.59 3.09zm3.62-4.94c-.2-.1-1.17-.58-1.35-.64-.18-.07-.32-.1-.45.1-.13.2-.5.64-.62.77-.11.13-.22.15-.42.05a5.42 5.42 0 0 1-1.6-.99 6 6 0 0 1-1.1-1.37c-.12-.2-.01-.31.08-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.34.06-.13.03-.25-.02-.35-.05-.1-.45-1.08-.61-1.47-.16-.38-.32-.33-.44-.33l-.38-.01c-.13 0-.34.05-.52.25-.18.2-.68.67-.68 1.63s.7 1.89.8 2.02c.1.13 1.38 2.1 3.34 2.95.47.2.83.32 1.11.41.47.15.9.13 1.23.08.38-.06 1.17-.48 1.33-.94.16-.46.16-.86.12-.94-.05-.08-.18-.13-.38-.23z" />
  </svg>
);

const SubstackIcon = ({ size = 18 }: { size?: number }) => (
  // Substack's mark: three stacked bars, the lowest folding into a page.
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.5 3h17v2.6h-17zM3.5 7.7h17v2.6h-17zM3.5 12.4v8.6l8.5-4.8 8.5 4.8v-8.6z" />
  </svg>
);

const YouTubeIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21.6 7.2a2.5 2.5 0 0 0-1.75-1.77C18.25 5 12 5 12 5s-6.25 0-7.85.43A2.5 2.5 0 0 0 2.4 7.2C2 8.82 2 12 2 12s0 3.18.4 4.8a2.5 2.5 0 0 0 1.75 1.77C5.75 19 12 19 12 19s6.25 0 7.85-.43a2.5 2.5 0 0 0 1.75-1.77C22 15.18 22 12 22 12s0-3.18-.4-4.8zM10 15.2V8.8l5.4 3.2z" />
  </svg>
);

const TikTokIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82A4.28 4.28 0 0 0 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-1.84-2.48V9.77a5.7 5.7 0 1 0 4.94 5.64V8.9a7.32 7.32 0 0 0 4.28 1.38V7.19a4.28 4.28 0 0 1-3.23-1.37z" />
  </svg>
);

const PLATFORMS: SocialPlatform[] = [
  {
    name: 'Instagram',
    handle: '@' + INSTAGRAM_HANDLE,
    href: INSTAGRAM_URL,
    comingSoon: false,
    icon: <InstagramIcon />,
  },
  {
    // Facebook mirrors Instagram in the content strategy — same posts,
    // adapted minimally. Listed second so the visual pairing of the
    // Meta-family platforms is clear in the icon row.
    name: 'Facebook',
    handle: 'EntreDash',
    href: FACEBOOK_URL,
    comingSoon: false,
    icon: <FacebookIcon />,
  },
  {
    name: 'X',
    handle: '@Entre_Dash',
    href: X_URL,
    comingSoon: false,
    icon: <XIcon />,
  },
  {
    name: 'LinkedIn',
    handle: 'EntreDash',
    href: LINKEDIN_URL,
    comingSoon: false,
    icon: <LinkedInIcon />,
  },
  {
    name: 'Reddit',
    handle: 'u/EntreDash',
    href: REDDIT_URL,
    comingSoon: false,
    icon: <RedditIcon />,
  },
  {
    name: 'WhatsApp',
    handle: 'Chat with us',
    href: WHATSAPP_URL,
    comingSoon: false,
    icon: <WhatsAppIcon />,
  },
  {
    // The publication the blog syndicates to. All three URLs below were checked
    // live (HTTP 200) before shipping, rather than assembled from a guessed
    // handle — a dead social link in a global footer is on every page.
    name: 'Substack',
    handle: 'entredash.substack.com',
    href: SUBSTACK_URL,
    comingSoon: false,
    icon: <SubstackIcon />,
  },
  {
    name: 'YouTube',
    handle: '@EntreDash',
    href: YOUTUBE_URL,
    comingSoon: false,
    icon: <YouTubeIcon />,
  },
  {
    name: 'TikTok',
    handle: '@entre_dash',
    href: TIKTOK_URL,
    comingSoon: false,
    icon: <TikTokIcon />,
  },
];

/* ---------- Footer variant ---------- */

/**
 * Compact button-style icon row for the global footer. Each platform
 * gets a 36x36 hit area with the icon centered. Brass color, with a
 * subtle brass background filling in on hover for live platforms.
 *
 * Coming-soon platforms render at 50% opacity with text-decorative
 * color — visibly muted but not so faint they look broken. Hover
 * does nothing and cursor is default, so the user gets immediate
 * feedback that the icon isn't actionable.
 *
 * The original design had a hover-reveal-handle behavior (the
 * handle text appeared next to the icon on hover). It was clunky
 * — created layout shifts and inconsistent alignment with adjacent
 * icons. Tooltips via the native `title` attribute do the same
 * job without the layout problems.
 */
export function SocialLinksFooter() {
  return (
    // Grid, not a wrapping flex row. A flex row wraps to whatever fits and
    // orphans the 6th icon (WhatsApp) alone on a second line — the "5 + 1"
    // look. A fixed-column grid always splits the six evenly: 3 + 3 on phones,
    // a single row of 6 once there's room (>=520px). See .social-footer-row.
    <div
      role="group"
      aria-label="Follow EntreDash on social media"
      className="social-footer-row"
    >
      {PLATFORMS.map((p) => (
        <SocialIconLink key={p.name} platform={p} />
      ))}
    </div>
  );
}

function SocialIconLink({ platform }: { platform: SocialPlatform }) {
  const [hovered, setHovered] = useState(false);
  const isLive = !platform.comingSoon && platform.href;
  const ariaLabel = isLive
    ? `Follow EntreDash on ${platform.name}`
    : `${platform.name} (coming soon)`;

  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Size comes from a CSS variable set on the row (.social-footer-row) so
    // the six icons can shrink on narrow phones and still hold a single line.
    // Falls back to 52px if the variable isn't present.
    width: 'var(--sbtn, 52px)',
    height: 'var(--sbtn, 52px)',
    flexShrink: 0,
    borderRadius: 999,
    // Live icons sit on a faint always-on brass tint so they read as
    // tappable affordances at rest, not flat marks. The tint gets
    // stronger on hover. Coming-soon icons stay transparent so the
    // visual hierarchy clearly distinguishes "follow this now" from
    // "this is a brand we'll have someday".
    background: isLive
      ? hovered
        ? 'rgba(181, 133, 60, 0.18)'
        : 'rgba(181, 133, 60, 0.08)'
      : // Coming-soon icons keep a faint neutral circle so the row's rhythm
        // stays even (no "missing button" gap), just muted vs the brass-tinted
        // live ones.
        'rgba(15, 23, 20, 0.05)',
    color: isLive
      ? hovered
        ? 'var(--brass-deep)'
        : 'var(--brass)'
      : 'var(--text-decorative)',
    opacity: isLive ? 1 : 0.5,
    transition: 'background-color 180ms ease, color 180ms ease, transform 180ms ease',
    transform: isLive && hovered ? 'translateY(-1px)' : 'none',
    cursor: isLive ? 'pointer' : 'default',
    textDecoration: 'none',
  };

  // Render the icon at the configured size inside the button. Footer
  // buttons are 52px hit areas with 26px icons — the social row is now
  // the sole follow affordance (the "Follow [wordmark]" label was
  // removed), so the icons carry it and are sized up for prominence.
  const sizedIcon = (() => {
    if (platform.name === 'Instagram') return <InstagramIcon size={26} />;
    if (platform.name === 'Facebook') return <FacebookIcon size={26} />;
    if (platform.name === 'X') return <XIcon size={26} />;
    if (platform.name === 'LinkedIn') return <LinkedInIcon size={26} />;
    // Reddit and WhatsApp glyphs carry more internal padding (circular
    // logo / speech bubble), so they read smaller at 26 — bump them up a
    // couple px so they optically match the other marks in the circle.
    if (platform.name === 'Reddit') return <RedditIcon size={30} />;
    // WhatsApp's bubble glyph carries the most internal padding, so it reads
    // smallest at a shared size — bump it to optically match the others.
    if (platform.name === 'WhatsApp') return <WhatsAppIcon size={34} />;
    // Substack's bars and TikTok's note both read large at a shared size
    // because they fill their viewBox edge to edge; YouTube's pill is wide and
    // short so it needs the extra couple of px to match visually.
    if (platform.name === 'Substack') return <SubstackIcon size={23} />;
    if (platform.name === 'YouTube') return <YouTubeIcon size={28} />;
    if (platform.name === 'TikTok') return <TikTokIcon size={24} />;
    return platform.icon;
  })();

  if (!isLive) {
    return (
      <span
        aria-label={ariaLabel}
        title={`${platform.name} — coming soon`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={buttonStyle}
      >
        {sizedIcon}
      </span>
    );
  }

  return (
    <a
      href={platform.href!}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      title={`${platform.name} · ${platform.handle}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={buttonStyle}
    >
      {sizedIcon}
    </a>
  );
}

/* ---------- Card variant ---------- */

/**
 * Bordered card layout for the Settings profile page. Same brand
 * vocabulary as other Settings cards on the page. Each platform
 * gets a row with icon, name, handle, and (for live ones) a Follow
 * action.
 */
export function SocialLinksCard() {
  return (
    <div
      style={{
        padding: '24px',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 12,
        marginTop: 32,
      }}
    >
      <div
        className="f-mono"
        style={{
          fontSize: 10,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--brass-deep)',
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Follow EntreDash
      </div>
      <h3
        className="f-heading"
        style={{
          fontSize: 18,
          fontWeight: 500,
          margin: '0 0 8px',
          color: 'var(--ink)',
        }}
      >
        Stay in the loop on socials
      </h3>
      <p
        style={{
          fontSize: 13,
          color: 'var(--text-decorative)',
          lineHeight: 1.55,
          margin: '0 0 18px',
        }}
      >
        Product walkthroughs on YouTube, short explainers on Instagram and
        TikTok, and the long-form writing on Substack. We post the work we would
        want to follow.
      </p>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {PLATFORMS.map((p) => (
          <SocialCardRow key={p.name} platform={p} />
        ))}
      </ul>
    </div>
  );
}

function SocialCardRow({ platform }: { platform: SocialPlatform }) {
  const inner = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 14px',
        background: 'var(--surface-page)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 8,
        transition:
          'border-color 180ms ease, background-color 180ms ease',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: platform.comingSoon
            ? 'rgba(181, 133, 60, 0.5)'
            : 'var(--brass-deep)',
          flexShrink: 0,
        }}
      >
        {platform.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--f-serif)',
            fontSize: 15,
            fontWeight: 500,
            color: 'var(--ink)',
            lineHeight: 1.3,
          }}
        >
          {platform.name}
        </div>
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: 11,
            color: platform.comingSoon
              ? 'var(--text-decorative)'
              : 'var(--brass-deep)',
            letterSpacing: '0.02em',
            marginTop: 2,
          }}
        >
          {platform.handle}
        </div>
      </div>
      {!platform.comingSoon && (
        <span
          className="f-mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--brass-deep)',
            fontWeight: 700,
            padding: '6px 12px',
            border: '1px solid var(--brass)',
            borderRadius: 999,
            flexShrink: 0,
          }}
        >
          Follow →
        </span>
      )}
      {platform.comingSoon && (
        <span
          className="f-mono"
          style={{
            fontSize: 9,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--text-decorative)',
            fontWeight: 700,
            padding: '4px 10px',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 999,
            flexShrink: 0,
          }}
        >
          Coming soon
        </span>
      )}
    </div>
  );

  if (platform.comingSoon || !platform.href) {
    return <li>{inner}</li>;
  }

  return (
    <li>
      <a
        href={platform.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Follow EntreDash on ${platform.name}`}
        style={{ display: 'block', textDecoration: 'none' }}
      >
        {inner}
      </a>
    </li>
  );
}
